import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { events as evtApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Tag, Calendar, MapPin, Loader2, Ticket, Search, Percent } from 'lucide-react';
import AppImage from '../components/AppImage';

// Cross-event resale marketplace — every ticket currently on public sale at 25% off.
// Waitlist-reserved listings are hidden until they flip to public (48h TTL, then auto-flip).
export default function ResaleMarketplace() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [buyingId, setBuyingId] = useState(null);

  useEffect(() => {
    evtApi.resaleMarketplace().then(setRows).catch(() => setRows([]));
  }, []);

  const buy = async (r) => {
    setBuyingId(r.id);
    try {
      const s = await evtApi.buyResale(r.eventId, r.id, window.location.origin);
      if (!s?.url) throw new Error('Stripe returned no URL');
      window.location.href = s.url;
    } catch (err) {
      toast({ title: 'Checkout failed', description: err?.response?.data?.detail || err?.message || 'Try again' });
      setBuyingId(null);
    }
  };

  if (rows === null) return (
    <div className="flex items-center gap-2 text-gray-400" data-testid="resale-marketplace-loading">
      <Loader2 className="animate-spin" size={16}/> Loading resale market…
    </div>
  );

  const norm = (s) => (s || '').toLowerCase();
  const term = q.trim().toLowerCase();
  const visible = term
    ? rows.filter(r => norm(r.event?.title).includes(term)
                    || norm(r.event?.city).includes(term)
                    || norm(r.event?.location).includes(term))
    : rows;

  return (
    <div className="space-y-5" data-testid="resale-marketplace-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Percent size={22} className="text-yellow-400"/> Resale market — 25% off
        </h1>
        <p className="text-gray-400 text-sm">Last-minute tickets released by other PBB members. Every sale funds the community and youth funds.</p>
      </div>

      {rows.length > 0 && (
        <div className="relative max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by event, city…"
            data-testid="resale-search-input"
            className="w-full pl-9 pr-3 py-2 bg-black border border-[#2a2a2a] rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:border-green-500"/>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="pbb-card p-10 text-center" data-testid="resale-marketplace-empty">
          <Tag className="mx-auto text-yellow-400" size={28}/>
          <div className="mt-3 font-semibold text-white">No resale tickets right now</div>
          <p className="text-sm text-gray-400 mt-1">Check back later — when members list a paid ticket, it shows up here at 25% off.</p>
          <Link to="/events" className="pbb-btn-green mt-5 inline-flex max-w-xs">Browse events</Link>
        </div>
      ) : visible.length === 0 ? (
        <div className="pbb-card p-8 text-center text-gray-400 text-sm" data-testid="resale-search-empty">
          No listings match &ldquo;{q}&rdquo;.
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resale-marketplace-list">
          {visible.map(r => {
            const ev = r.event || {};
            const original = Number(r.priceUsd || 0);
            const discount = Number(r.publicPriceUsd || 0);
            const savings = Math.max(0, original - discount);
            return (
              <li key={r.id} className="pbb-card overflow-hidden flex flex-col" data-testid={`resale-row-${r.id}`}>
                <AppImage src={ev.image} alt={ev.title} rounded="none" fallback={<Ticket size={24}/>}/>
                <div className="p-4 flex flex-col flex-1">
                  <div className="font-semibold text-white truncate" title={ev.title}>{ev.title || 'Event'}</div>
                  <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-3">
                    {ev.date && <span className="inline-flex items-center gap-1"><Calendar size={11}/> {ev.date}{ev.time ? ` · ${ev.time}` : ''}</span>}
                    {(ev.location || ev.city) && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {[ev.location, ev.city].filter(Boolean).join(', ')}</span>}
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <div className="text-2xl font-bold text-green-400">${discount.toFixed(2)}</div>
                    <div className="text-xs text-gray-500 line-through">${original.toFixed(2)}</div>
                    <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-500/40">25% OFF</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1">Save ${savings.toFixed(2)} · Listed by {r.sellerDisplayName || 'a PBB member'}</div>
                  <button onClick={() => buy(r)} disabled={buyingId === r.id}
                    data-testid={`resale-buy-${r.id}`}
                    className="mt-3 w-full px-3 py-2 rounded-lg text-sm inline-flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-60">
                    {buyingId === r.id ? <><Loader2 size={14} className="animate-spin"/> Redirecting…</> : <><Tag size={14}/> Buy at ${discount.toFixed(2)}</>}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
